import { useCallback } from 'react';
import { FaxHooks, FaxV1 } from '@frontend/api-fax';
import { useTranslation } from '@frontend/i18n';
import { useFaxNotification } from '@frontend/notifications';
import { useAppScopeSelection } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { GetWeavePopNotificationByType } from '@frontend/types';
import { FaxEventPayload, GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

// Type override for action method fax event. To be removed when backend support location_id in this event's payload params.
type FaxEventParamsOverride = FaxEventPayload['params'] & { locationID: string };

export const useFaxEventSubscription = () => {
  const { create } = useFaxNotification();
  const { t } = useTranslation('notifications');
  const { addToListFaxCache, updateFaxInListFaxCache } = FaxV1.FaxQueryUpdaters.useListFaxQueryUpdater();
  const invalidateFaxCount = FaxHooks.useInvalidateFaxCount();
  const invalidateFaxSearch = FaxV1.FaxQueryUpdaters.useInvalidateFaxSearch();
  const { getFeatureFlagValue } = useAppFlagStore();
  const { selectedLocationIds } = useAppScopeSelection();

  const createInboundFaxNotification = useCallback(
    (id: string, params: FaxEventPayload['params']) => {
      const notification: GetWeavePopNotificationByType<'fax-new'> = {
        id,
        timestamp: Date.now(),
        type: 'fax-new',
        payload: {
          faxId: params.fax_id,
          action: params.action,
          from: params.from,
          to: params.to,
        },
        state: {
          paused: false,
          status: 'unread',
        },
      };

      create(notification, {
        os: {
          title: t('Fax'),
          body: params.from,
        },
      });
    },
    [create, t]
  );

  const createFailedFaxNotification = useCallback(
    (id: string, params: FaxEventPayload['params']) => {
      const notification: GetWeavePopNotificationByType<'fax-new'> = {
        id,
        timestamp: Date.now(),
        type: 'fax-new',
        payload: {
          faxId: params.fax_id,
          action: params.action,
          from: params.from,
          to: params.to,
        },
        state: {
          paused: false,
          status: 'unread',
        },
      };

      create(notification, {
        os: {
          title: t('Fax'),
          body: params.from,
        },
      });
    },
    [create, t]
  );

  const handler: GetWebsocketEventHandler<'FaxNotification'> = useCallback(
    (payload) => {
      const { params } = payload;

      const locationId = params.location_id || (params as FaxEventParamsOverride).locationID;

      // BE will send fax events to all users - location agnostic,
      // so we need to check if the event is relevant to the user's selected locations before processing it.
      if (!!locationId && !selectedLocationIds.includes(locationId)) {
        return;
      }

      if (params.action === 'inbound') {
        addToListFaxCache({ faxId: params.fax_id, locationId });
        createInboundFaxNotification(payload.id, params);
        invalidateFaxCount();
        invalidateFaxSearch();
      }

      if (params.action === 'status') {
        const hasFaxAsyncSendFlow = getFeatureFlagValue({ locationIds: [locationId], flagName: 'fax-async-send-flow' });
        if (params.status === 'sent' && hasFaxAsyncSendFlow) {
          addToListFaxCache({ faxId: params.fax_id, locationId });
        } else {
          updateFaxInListFaxCache({
            faxId: params.fax_id,
            locationId,
          });
        }
        invalidateFaxSearch();
        if (params.status === 'failed') {
          createFailedFaxNotification(payload.id, params);
        }
      }
    },
    [
      addToListFaxCache,
      createInboundFaxNotification,
      updateFaxInListFaxCache,
      createFailedFaxNotification,
      invalidateFaxCount,
      invalidateFaxSearch,
      getFeatureFlagValue,
      selectedLocationIds,
    ]
  );

  return useWebsocketEventSubscription('FaxNotification', handler);
};
